import { useId, useState } from 'react'
import { isAfterToday, tomorrow } from '../../account/dates'
import { actionsFor, cancellationReasonLabel, defaultResumeDay } from '../../account/subscription'
import type { Household } from '../../api/households'
import {
  CANCELLATION_REASONS,
  type CancellationReason,
  type Subscription,
} from '../../api/subscriptions'
import { firstName, possessive, type User } from '../../api/users'
import { Choice } from '../ui/Choice'
import { TextButton } from '../ui/TextButton'
import { TextField } from '../ui/TextField'
import { SettingForm } from './Setting'
import { useSubscriptionEditor } from './useSubscriptionEditor'

interface ManageSubscriptionProps {
  subscription: Subscription
  child: User
  household: Household
  /** Where a pause or a cancellation leaves its word for the schedule row; see SubscriptionRows. */
  setOutcome(outcome: string | null): void
}

/**
 * The rare, consequential actions, receded to text beneath the rows: pause
 * until a day, or cancel with a reason. Each opens its form in place of the
 * links, and the copy promises nothing beyond what stops and when.
 */
export function ManageSubscription({
  subscription,
  child,
  household,
  setOutcome,
}: ManageSubscriptionProps) {
  const actions = actionsFor(subscription)
  const possessiveName = possessive(firstName(child))
  const groupName = useId()

  const [resumeOn, setResumeOn] = useState('')
  const pause = useSubscriptionEditor({
    subscription,
    household,
    change: () => ({ status: 'paused', resumeOn }),
    validate: () => (isAfterToday(resumeOn) ? null : 'Choose a day after today.'),
    setOutcome,
  })

  const [reason, setReason] = useState<CancellationReason | null>(null)
  const [comments, setComments] = useState('')
  const cancel = useSubscriptionEditor({
    subscription,
    household,
    change: () => ({
      status: 'cancelled',
      reason: reason ?? 'other',
      comments: comments.trim() || undefined,
    }),
    validate: () => (reason ? null : 'Choose a reason.'),
    setOutcome,
  })

  function startPause() {
    setResumeOn(defaultResumeDay(subscription))
    pause.start()
  }

  function startCancel() {
    setReason(null)
    setComments('')
    cancel.start()
  }

  if (!actions.pause && !actions.cancel) return null

  if (pause.open) {
    return (
      <SettingForm editor={pause} submitLabel="Pause">
        <p className="text-base text-ink">
          {subscription.tier === 'kit' ? 'Kits and charges' : 'Charges'} stop until the day you
          pick.
        </p>
        <TextField
          type="date"
          label="Resume on"
          labelStyle="floating"
          min={tomorrow()}
          required
          invalid={pause.error !== null}
          value={resumeOn}
          onChange={(event) => setResumeOn(event.target.value)}
          data-testid="pause-until"
        />
      </SettingForm>
    )
  }

  if (cancel.open) {
    return (
      <SettingForm
        editor={cancel}
        submitLabel="Cancel subscription"
        cancelLabel="Keep subscription"
      >
        <p className="text-base text-ink">
          This cancels the subscription right away. You can restart it any time.
        </p>
        <fieldset className="flex flex-col">
          <legend className="pb-1 text-sm text-ink-soft">Why are you cancelling?</legend>
          {CANCELLATION_REASONS.map((candidate) => (
            <Choice
              key={candidate}
              type="radio"
              name={groupName}
              value={candidate}
              checked={reason === candidate}
              onChange={() => setReason(candidate)}
              data-testid={`reason-${candidate}`}
            >
              {cancellationReasonLabel(candidate, firstName(child))}
            </Choice>
          ))}
        </fieldset>
        <TextField
          label="Anything else you’d like us to know?"
          labelStyle="floating"
          value={comments}
          onChange={(event) => setComments(event.target.value)}
          data-testid="cancel-comments"
        />
      </SettingForm>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-4 py-3" data-testid="manage-subscription">
      {actions.pause && (
        <TextButton
          ref={pause.editButton}
          onClick={startPause}
          aria-label={`Pause ${possessiveName} subscription`}
          data-testid="pause-subscription"
        >
          Pause subscription
        </TextButton>
      )}
      {actions.cancel && (
        <TextButton
          ref={cancel.editButton}
          onClick={startCancel}
          aria-label={`Cancel ${possessiveName} subscription`}
          data-testid="cancel-subscription"
        >
          Cancel subscription
        </TextButton>
      )}
    </div>
  )
}
